/**
 * @graphql cc
 */
import type React from 'react';
import { useEffect, useState } from 'react';
import gql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';

import { abortAll } from '@atlaskit/react-ufo/interaction-metrics';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop';

import { CommentType } from '@confluence/comments-data/entry-points/useCommentsData';
import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';
import {
	handleFooterCommentsNavigation,
	ScrollBehavior,
} from '@confluence/comments-util/entry-points/commentsNavigationUtils';

import { useShowCommentsPanel } from '@confluence/comments-panel/entry-points/useShowCommentsPanel';
import { LocationValue } from '@confluence/comments-panel-utils/entry-points/commentsPanelHelper';
import {
	ViewValues,
	useCommentsPanelActions,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import { getLogger } from '@confluence/logger';
import { createSingleQueryParamHook } from '@confluence/route-manager/entry-points/RouteState';

import type {
	ObjectCommentQueryParamsListenerObjectCommentQueryParamsQuery as ObjectCommentQueryParamsListenerObjectCommentQueryParamsQuery$data,
	ObjectCommentQueryParamsListenerObjectCommentQueryParamsQueryVariables as ObjectCommentQueryParamsListenerObjectCommentQueryParamsQuery$variables,
	ObjectCommentQueryParamsListenerObjectCommentQueryParamsQuery_comments_PaginatedCommentList_nodes_Comment_location_FooterComment as FooterCommentLocation,
} from './__types__/ObjectCommentQueryParamsListenerObjectCommentQueryParamsQuery';

const ObjectCommentQueryParamsListenerObjectCommentQueryParamsQuery = gql`
	query ObjectCommentQueryParamsListenerObjectCommentQueryParamsQuery($commentId: ID!) {
		comments(commentId: $commentId) {
			nodes {
				id
				location {
					type
					... on FooterComment {
						commentResolveProperties {
							resolved
						}
					}
				}
			}
		}
	}
`;

const logger = getLogger('inline-comments:query-param-listener');

const useFocusedCommentIdQueryParam = createSingleQueryParamHook('focusedCommentId');
const useReplyToCommentQueryParam = createSingleQueryParamHook('replyToComment');

// If there is a link to non-existent (probably deleted) comment, we don't want to show an error
const expectedErrorCodes = [404];

const isExpectedError = (error: ApolloErrorInterop) =>
	error.graphQLErrors.some((e) => expectedErrorCodes.includes(e?.extensions?.statusCode));

type ObjectCommentQueryParamsListenerProps = {
	hasLoadedObjectComments: boolean;
	allObjectCommentsRendered: boolean;
	commentBatchRendered?: boolean;
	commentBatchCount?: number;
	setForceLoadComments: (value: React.SetStateAction<boolean>) => void;
};

export const ObjectCommentQueryParamsListener: React.FC<ObjectCommentQueryParamsListenerProps> = ({
	hasLoadedObjectComments,
	allObjectCommentsRendered,
	commentBatchRendered,
	commentBatchCount,
	setForceLoadComments,
}) => {
	const [hasScrolledToFocusedComment, setHasScrolledToFocusedComment] = useState<boolean>(false);
	const [hasScrolledToReplyComment, setHasScrolledToReplyComment] = useState<boolean>(false);

	const { setCurrentlySelectedThreadKey, setReplyToCommentId } = useCommentsPanelActions();
	const { showCommentsPanel } = useShowCommentsPanel();

	const focusedCommentId = useFocusedCommentIdQueryParam();
	const replyToComment = useReplyToCommentQueryParam();

	const commentId = focusedCommentId || replyToComment;

	const { data, error } = useQuery<
		ObjectCommentQueryParamsListenerObjectCommentQueryParamsQuery$data,
		ObjectCommentQueryParamsListenerObjectCommentQueryParamsQuery$variables
	>(ObjectCommentQueryParamsListenerObjectCommentQueryParamsQuery, {
		variables: {
			commentId: commentId!,
		},
		skip: !commentId,
	});
	if (error && !isExpectedError(error)) {
		logger.error`An error occurred when querying the focused object comment status; ${error}`;
	}

	let isResolved: boolean | undefined = undefined;
	// If the commentId is not present, error is defined and isResolved will be undefined
	if (data && !error) {
		const location = data?.comments?.nodes?.[0]?.location as FooterCommentLocation;
		isResolved = Boolean(location?.commentResolveProperties?.resolved);
	}

	// Handle scrolling to focused comment or replyTo comment after the data is loaded
	useEffect(() => {
		if (
			// Note that if the commentId is not found, the query will return an error and we don't
			// have to scroll to the comment
			commentId &&
			data &&
			!error &&
			hasLoadedObjectComments &&
			allObjectCommentsRendered &&
			!hasScrolledToFocusedComment &&
			!hasScrolledToReplyComment
		) {
			if (isResolved) {
				showCommentsPanel({ openWithView: ViewValues.RESOLVED });
				setCurrentlySelectedThreadKey(commentId, CommentType.GENERAL);
				if (focusedCommentId) {
					setHasScrolledToFocusedComment(true);
				} else {
					setHasScrolledToReplyComment(true);
				}
			} else {
				const commentElementId = `footer-comment-thread-${focusedCommentId || replyToComment}-container`;
				const replyCommentElementId = `footer-comment-thread-${focusedCommentId || replyToComment}-replies`;

				const commentElement = document.getElementById(commentElementId);
				const replyCommentElement = document.getElementById(replyCommentElementId);
				const element = commentElement || replyCommentElement;
				if (element) {
					if (replyToComment) {
						setCurrentlySelectedThreadKey(replyToComment);
						setReplyToCommentId({
							parentCommentId: replyToComment,
							location: LocationValue.FOOTER,
						});
						setHasScrolledToReplyComment(true);
						return;
					}
					const rect = element.getBoundingClientRect();
					const commentIsInViewport =
						rect.top >= 0 &&
						rect.left >= 0 &&
						rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
						rect.right <= (window.innerWidth || document.documentElement.clientWidth);

					if (!commentIsInViewport) {
						// If the document scrolls to a focused comment on View or Live Page, the VC event is aborted.
						abortAll('new_interaction', `document-scroll:object-comment-container`);

						// Use unified navigation if feature gate is enabled, otherwise use direct scrolling
						if (commentId && fg('confluence_comments_unified_navigation')) {
							if (expValEquals('confluence_comment_deeplink_delay_scroll', 'isEnabled', true)) {
								setTimeout(() => {
									handleFooterCommentsNavigation({
										scrollBehavior: ScrollBehavior.SMOOTH,
										block: 'center',
										inline: 'center',
										commentId,
									});
								}, 4000);
							} else {
								handleFooterCommentsNavigation({
									scrollBehavior: ScrollBehavior.SMOOTH,
									block: 'center',
									inline: 'center',
									commentId,
								});
							}
						} else {
							element.scrollIntoView({
								behavior: ScrollBehavior.SMOOTH,
								block: 'center',
								inline: 'center',
							});
						}

						setHasScrolledToFocusedComment(true);
						setCurrentlySelectedThreadKey(focusedCommentId);
					}
				}
			}
		}
	}, [
		data,
		error,
		hasLoadedObjectComments,
		commentId,
		focusedCommentId,
		replyToComment,
		allObjectCommentsRendered,
		commentBatchRendered,
		commentBatchCount,
		setCurrentlySelectedThreadKey,
		setReplyToCommentId,
		isResolved,
		showCommentsPanel,
		hasScrolledToFocusedComment,
		hasScrolledToReplyComment,
	]);

	// Reset scroll tracking when focusedCommentId or replyToComment changes
	useEffect(() => {
		setHasScrolledToFocusedComment(false);
		setHasScrolledToReplyComment(false);
	}, [focusedCommentId, replyToComment]);

	// Force load comments if focusedCommentId or replayToComment is present
	useEffect(() => {
		if ((focusedCommentId || replyToComment) && !hasLoadedObjectComments) {
			setForceLoadComments(true);
		}
	}, [focusedCommentId, replyToComment, hasLoadedObjectComments, setForceLoadComments]);

	return null;
};
